import type { AccountSubtype, AccountType } from '@/api/accounts';

export interface MappedType {
  type: AccountType;
  subtype: AccountSubtype;
}

/**
 * The Plaid provider's own `type`/`subtype` names (what tmm-api sends as `providerType` /
 * `providerSubtype` for a PLAID connection) → our ledger type/subtype (TMMAPP-0024 §4).
 *
 * | Plaid type.subtype        | Ours              |
 * |---------------------------|-------------------|
 * | depository.checking       | CASH / CHECKING   |
 * | depository.savings        | CASH / SAVINGS    |
 * | credit.credit card        | REVOLVING / CREDIT_CARD |
 * | loan.mortgage             | LOAN / MORTGAGE   |
 * | loan.* (other)            | LOAN / SIMPLE     |
 * | else                      | CASH / OTHER      |
 */
export function mapPlaidType(rawType?: string | null, rawSubtype?: string | null): MappedType {
  const type = (rawType ?? '').trim().toLowerCase();
  const subtype = (rawSubtype ?? '').trim().toLowerCase().replace(/_/g, ' ');
  if (type === 'depository' && subtype === 'checking') return { type: 'CASH', subtype: 'CHECKING' };
  if (type === 'depository' && subtype === 'savings') return { type: 'CASH', subtype: 'SAVINGS' };
  if (type === 'credit' && subtype === 'credit card')
    return { type: 'REVOLVING', subtype: 'CREDIT_CARD' };
  if (type === 'loan' && subtype === 'mortgage') return { type: 'LOAN', subtype: 'MORTGAGE' };
  if (type === 'loan') return { type: 'LOAN', subtype: 'SIMPLE' };
  return { type: 'CASH', subtype: 'OTHER' };
}

/**
 * Stripe Financial Connections `category`/`subcategory` → our ledger type/subtype (TMMAPP-0052,
 * tmm-api's table, which the server enforces too). `null` = not mappable yet (file import only).
 *
 * | Stripe category.subcategory      | Ours                       |
 * |----------------------------------|----------------------------|
 * | cash.checking                    | CASH / CHECKING            |
 * | cash.savings                     | CASH / SAVINGS             |
 * | cash.* (other)                   | CASH / OTHER               |
 * | credit.credit_card               | REVOLVING / CREDIT_CARD    |
 * | credit.line_of_credit            | REVOLVING / LINE_OF_CREDIT |
 * | credit.mortgage                  | LOAN / MORTGAGE            |
 * | investment.*, other.*, credit.*  | not mappable               |
 */
export function mapStripeType(
  category?: string | null,
  subcategory?: string | null,
): MappedType | null {
  const type = (category ?? '').trim().toLowerCase();
  const subtype = (subcategory ?? '').trim().toLowerCase();
  if (type === 'cash' && subtype === 'checking') return { type: 'CASH', subtype: 'CHECKING' };
  if (type === 'cash' && subtype === 'savings') return { type: 'CASH', subtype: 'SAVINGS' };
  if (type === 'cash') return { type: 'CASH', subtype: 'OTHER' };
  if (type === 'credit' && subtype === 'credit_card')
    return { type: 'REVOLVING', subtype: 'CREDIT_CARD' };
  if (type === 'credit' && subtype === 'line_of_credit')
    return { type: 'REVOLVING', subtype: 'LINE_OF_CREDIT' };
  if (type === 'credit' && subtype === 'mortgage') return { type: 'LOAN', subtype: 'MORTGAGE' };
  return null;
}

/** The per-provider mapper; `null` = not mappable (only ever for Stripe today). */
export function mapProviderType(
  provider: 'PLAID' | 'STRIPE_FC',
  type?: string | null,
  subtype?: string | null,
): MappedType | null {
  return provider === 'STRIPE_FC' ? mapStripeType(type, subtype) : mapPlaidType(type, subtype);
}
